import BaseService from '@/services/BaseService'
import {
  User,
  SchoolStaffNonNormalized,
  TeacherNonNormalized,
  SubstituteNonNormalized,
  StudentNonNormalized,
  UnknownNonNormalized, GenericUser,
} from '@/models/user'
import Establishment from '@/models/establishment'
import {Dictionary} from 'vue-router/types/router'
import { UserHistory } from '@/models/userHistory'
interface UrlsMapping {
  student: string
  schoolStaff: string
}

class UserService extends BaseService<User> {
  constructor() {
    super('users/')
  }

  public async search(filter: string): Promise<GenericUser[]> {
    const result = await this.instance.get<GenericUser[]>(
      this.resourcePath + 'search/',
      {
        params: {
          filter,
        },
      },
    )
    return result.data
  }

  public async getUserHistory(adPersonne_id: number): Promise<UserHistory> {
    const result = await this.instance.get<UserHistory>(
        this.resourcePath + adPersonne_id + '/history/',
    )
    return result.data as any
  }

  public async getEstablishments(): Promise<Establishment[]> {
    const result = await this.instance.get<Establishment[]>(
      this.resourcePath + 'schools/',
    )
    return result.data
  }

  public async getUsersFromEstablishment(establishmentId: string): Promise<User[]> {
    const result = await this.instance.get<User[]>(
      this.resourcePath + 'school-user/',
      {
        params: {
          establishmentId,
        },
      },
    )
    return result.data
  }

  public async assignServicesToUsers(services: any[], accounts: User[]) {
    const result = await this.instance.post(this.resourcePath + 'services/', {
      services,
      accounts,
    })
    return result.data as any
  }

  public async recoverUsername(email: string) {
    // TODO change
    const result = await this.instance.post(this.resourcePath + 'recover-username/', {
      email,
    })
    return result.data as any
  }

  public async changePassword(
    username: string | undefined,
    oldPassword: string,
    password: string,
  ) {
    // TODO change url
    const result = await this.instance.post(
      this.resourcePath + 'change-password/',
      {
        username,
        password,
        oldPassword,
      },
    )
    return result.data as any
  }

  public async resetPassword(tokenKey: string, password: string) {
    const result = await this.instance.post(
      this.resourcePath + 'reset-password/',
      {
        tokenKey,
        password,
      },
    )
    return result.data as any
  }

  public async loginRecovery(tokenKey: string, password: string) {
    const result = await this.instance.post(
      this.resourcePath + 'login-recovery/',
      {
        tokenKey,
        password,
      },
    )
    return result.data as any
  }

  public async recoverPassword(username: string) {
    // TODO change to get the good url, todo when creation of token will be done
    const result = await this.instance.post(
      this.resourcePath + 'recover-password/',
      {
        username,
      },
    )
    return result.data as any
  }

  public async resetUserPassword(
    account: User | GenericUser,
    schoolId: string,
    userType: keyof UrlsMapping,
  ) {
    const urlsObj: UrlsMapping = {
      student: 'reset-student-password/',
      schoolStaff: 'reset-school-staff-password/',
    }
    const result = await this.instance.post(
      this.resourcePath + urlsObj[userType],
      {
        account,
        schoolId,
      },
    )
    return result.data
  }

  public async resetUsersPassword(
    accounts: User[],
    schoolId: string,
    userType: keyof UrlsMapping,
  ) {
    const urlsObj: UrlsMapping = {
      student: 'reset-students-password/',
      schoolStaff: 'reset-school-staffs-password/',
    }

    const result = await this.instance.post(
      this.resourcePath + urlsObj[userType],
      {
        accounts,
        schoolId,
      },
    )
    return result.data as any
  }

  public async resetStudentsYearPassword(accounts: User[] | GenericUser[] , schoolId: string) {
    const result = await this.instance.post(
      this.resourcePath + 'reset-password-year/',
      {
        accounts,
        schoolId,
      },
    )
    return result.data as any
  }

  public async toggleWifi(
    ad_personne_id: number,
    username: string,
    fonctions: string,
    schoolId: string,
    wifi: number,
  ) {
    let endpoint
    if (fonctions.toLowerCase() === 'élève') {
      endpoint = 'toggle-student-wifi/'
    } else {
      endpoint = 'toggle-school-staff-wifi/'
    }

    const result = await this.instance.post(this.resourcePath + endpoint, {
      schoolId,
      ad_personne_id,
      username,
      wifi,
    })
    return result.data
  }

  public async getDefaultStudentsPasswords(studentIds: number[]) {
    const result = await this.instance.post(
      this.resourcePath + 'get-default-password/',
      {
        studentIds,
      },
    )
    return result.data
  }

  public async getDefaultStudentsPasswordsForSchool(schoolID: string) {
    const result = await this.instance.get(
      this.resourcePath + 'default-password-school/',
      {
        params: {
          schoolID,
        },
      },
    )
    return result.data
  }

  public async getMasksSuggestion(username: string): Promise<string[]> {
    const result = await this.instance.get(this.resourcePath + 'masks/', {
      params: {
        username,
      },
    })
    return result.data
  }

  public async enable(ad_personne_id: number, username: string) {
    const result = await this.instance.post(this.resourcePath + ad_personne_id + '/enable/', {username})
    return result.data
  }

  public async disable(ad_personne_id: number, username: string) {
    const result = await this.instance.post(this.resourcePath + ad_personne_id + '/disable/', {username})
    return result.data
  }

  public async changeActivationKey(ad_personne_id: number, username: string) {
    const result = await this.instance.post(this.resourcePath + ad_personne_id + '/change-activation-key/', {username})
    return result.data
  }

  public async updateOwnMask(
    mask: string,
    username: string,
  ): Promise<string[]> {
    const result = await this.instance.post(
      this.resourcePath + 'change-own-email/',
      {
        mask,
        username,
      },
    )
    return result.data
  }
  public async updateMask(mask: string, username: string): Promise<string[]> {
    const result = await this.instance.post(
      this.resourcePath + 'change-email/',
      {
        mask,
        username,
      },
    )
    return result.data
  }

  public async getStudentNonNormalized(): Promise<StudentNonNormalized[]> {
    const result = await this.instance.get(
      this.resourcePath + 'invalid-students/',
    )
    return result.data
  }

  public async getTeacherNonNormalized(): Promise<TeacherNonNormalized[]> {
    const result = await this.instance.get(
      this.resourcePath + 'invalid-teachers/',
    )
    return result.data
  }

  public async getSchoolStaffNonNormalized(): Promise<
    SchoolStaffNonNormalized[]
  > {
    const result = await this.instance.get(
      this.resourcePath + 'invalid-school-staff/',
    )
    return result.data
  }

  public async getSubstituteNonNormalized(): Promise<
    SubstituteNonNormalized[]
  > {
    const result = await this.instance.get(
      this.resourcePath + 'invalid-substitutes/',
    )
    return result.data
  }

  public async getUnknownNotNormalized(): Promise<UnknownNonNormalized[]> {
    const result = await this.instance.get(
      this.resourcePath + 'invalid-unknowns/',
    )
    return result.data
  }

  public async getAccountsWaitingForSync(): Promise<SubstituteNonNormalized[]> {
    const result = await this.instance.get(
      this.resourcePath + 'waiting-for-sync/',
    )
    return result.data
  }

  public async generateStudentsLabel(data): Promise<any> {
    const result = await this.instance.post(
      this.resourcePath + 'generate-students-label/',
      data, {responseType: 'blob'},
    )
    return result.data
  }
}

export default new UserService()
